import { useEffect } from "react";
import { absoluteUrl, DEFAULT_DESC, DEFAULT_IMAGE, DEFAULT_TITLE } from "@/lib/seo";
import { SEO_DEFAULTS, SITE } from "@/config/site.config";

/**
 * Per-page document head.
 *
 * Sets title, description, canonical, Open Graph and Twitter tags, and injects any
 * page-specific JSON-LD. Every tag it writes is marked `data-seo` so it can be cleaned
 * up on route change — otherwise stale structured data from the previous page lingers.
 *
 * The build-time prerenderer (`scripts/prerender.js`) snapshots the DOM after this has
 * run, so crawlers receive these tags in the initial HTML rather than after hydration.
 */

const upsertMeta = (key, attr, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEOHead = ({
  title,
  description = DEFAULT_DESC,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = [],
}) => {
  const fullTitle = title ? `${title} — Snapx Technologies` : DEFAULT_TITLE;
  const url = absoluteUrl(path);
  // Serialise so the effect re-runs when schema content changes, not on every render.
  const schemaKey = JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta("description", "name", description);
    // A staging deploy (REACT_APP_INDEXABLE=false) is noindex everywhere, regardless
    // of what an individual page asked for.
    const blocked = noindex || !SEO_DEFAULTS.indexable;
    upsertMeta("robots", "name", blocked ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1");

    upsertMeta("og:title", "property", fullTitle);
    upsertMeta("og:description", "property", description);
    upsertMeta("og:url", "property", url);
    upsertMeta("og:type", "property", type);
    upsertMeta("og:image", "property", image);
    // Without explicit dimensions several platforms fall back to a small thumbnail
    // rather than the large card, and WhatsApp may skip the image on first fetch.
    upsertMeta("og:image:width", "property", String(SITE.ogImageWidth));
    upsertMeta("og:image:height", "property", String(SITE.ogImageHeight));
    upsertMeta("og:image:type", "property", "image/png");
    upsertMeta("og:image:alt", "property", fullTitle);
    upsertMeta("og:site_name", "property", SITE.name);
    upsertMeta("og:locale", "property", "en_IN");

    upsertMeta("twitter:card", "name", "summary_large_image");
    upsertMeta("twitter:title", "name", fullTitle);
    upsertMeta("twitter:description", "name", description);
    upsertMeta("twitter:image", "name", image);
    upsertMeta("twitter:image:alt", "name", fullTitle);

    upsertLink("canonical", url);
  }, [fullTitle, description, url, type, image, noindex]);

  useEffect(() => {
    const blocks = JSON.parse(schemaKey);
    if (!blocks.length) return undefined;
    const nodes = blocks.map((block) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo", "page");
      el.textContent = JSON.stringify(block);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((el) => el.remove());
  }, [schemaKey]);

  return null;
};

export default SEOHead;
